import React, { useEffect, useMemo, useState } from "react";

import { chain } from "lodash";

import { useSalesBreakdownConfigs } from "@components/data/sales-breakdown-configs";
import {
  TimeSeriesChart,
  type TimeSeriesChartProps,
} from "@components/reports/charts";
import { Loading, NoDataView } from "@components/title-slide-view";
import { useUser } from "@components/user-context";
import {
  annotationPlugin,
  useBuildAnnotationDataset,
} from "@features/custom-annotations";
import { logEvent } from "@internal/analytics";
import {
  type GetSalesChartReport_me_salesReportChart_rows,
  type ChartableResponseRowWithChildren,
  MetricFormatters,
  type SalesPageMetric,
} from "@north-beam/nb-client-side";
import {
  type ColumnDef,
  isWithinDateInterval,
  isWithinDateIntervalHourly,
  iterateHourlyOverIntervalWithAdditionalAccuracy,
  iterateOverInterval,
  jsonHash,
  type Table,
  type TableRow,
  type TimeSeries,
  type TimeSeriesPoint,
  metrics,
  type CurrencyCode,
} from "@north-beam/nb-common";
import { leaves } from "@north-beam/nb-common";
import { ChartLegend } from "@pages/sales/chart-section/chart-legend";
import { CustomizeChartMetricsModal } from "@pages/sales/chart-section/customize-chart-metrics-modal";
import { useReportBodyState } from "@pages/sales/report-body-control";
import { useSalesViews } from "@pages/sales/sales-views-context";
import { normalizeMetricKey } from "@pages/sales/tab-table-section/table/table-utils";
import { ExportToCsv } from "@shared/buttons";

import { preprocessForChart } from "../utils/chart-utils";

interface ChartProps {
  chartableData: any;
  metricsArray: SalesPageMetric[];
}

interface ChartSectionProps {
  rows: GetSalesChartReport_me_salesReportChart_rows[];
  metricsArray: SalesPageMetric[];
  isLoading?: boolean;
}

export type LegendMetric<M extends metrics.MetricType = metrics.MetricType> =
  M extends metrics.MetricType
    ? {
        loading?: boolean;
        currentValue: number;
        compareValue: number;
        name: string;
        title?: string;
        isPositiveChangeGood: boolean;
        format: M;
        formatter: (v: number | null | undefined) => string;
        description?: string;
        hideComparisonFromTabDisplay: boolean;
        changeFractionAreaText?: string;
      }
    : never;

export const ChartSection = ({
  rows,
  metricsArray,
  isLoading,
}: ChartSectionProps) => {
  const { breakdowns } = useReportBodyState();
  const { value: breakdownConfigs } = useSalesBreakdownConfigs();
  const chartableData = useMemo(
    () => preprocessForChart(rows, breakdownConfigs, breakdowns),
    [rows, breakdowns, breakdownConfigs],
  );

  if (isLoading) {
    return (
      <div className="bg-white rounded-md border px-4 relative w-full h-[23rem]">
        <Loading className="w-[13rem]" />
      </div>
    );
  }

  if (!chartableData) {
    return <NoDataView />;
  }

  return <Chart chartableData={chartableData} metricsArray={metricsArray} />;
};

const DefaultLegendMetric = (code: CurrencyCode): LegendMetric<"dollars"> => ({
  loading: true,
  currentValue: 0,
  compareValue: 0,
  name: "Loading Metrics",
  isPositiveChangeGood: true,
  format: metrics.MetricTypes.CURRENCY,
  formatter: (v) =>
    MetricFormatters.currency(v, code, {
      precision: "approximate",
    }),
  hideComparisonFromTabDisplay: false,
});

export const Chart = ({ chartableData, metricsArray }: ChartProps) => {
  const { currentSalesViewEdit } = useSalesViews();
  const { user } = useUser();

  const { dateRange, compareDateRange, timeGranularity } = useReportBodyState();
  const [selectedMetricIndices, setSelectedMetricIndices] = useState([0]);
  const [graphMetricBoxes, setGraphMetricBoxes] = useState<string[]>([]);

  const annotationDataset = useBuildAnnotationDataset(
    dateRange.startDate,
    dateRange.endDate,
    user.timezone,
  );

  useEffect(() => {
    setGraphMetricBoxes(currentSalesViewEdit?.data.selectedMetricBoxes ?? []);
  }, [currentSalesViewEdit?.data?.selectedMetricBoxes]);

  const baseMetricLegends = useMemo(() => {
    const currentTotal = metrics.newMetricPoint();
    const compareTotal = metrics.newMetricPoint();

    for (const date in chartableData.series) {
      const point = chartableData.series[date];

      if (timeGranularity === "hourly") {
        if (isWithinDateIntervalHourly(date, dateRange, user.timezone)) {
          metrics.addToMetricPoint(currentTotal, point);
        }
        if (isWithinDateIntervalHourly(date, compareDateRange, user.timezone)) {
          metrics.addToMetricPoint(compareTotal, point);
        }
      } else {
        if (isWithinDateInterval(date.substring(0, 10), dateRange)) {
          metrics.addToMetricPoint(currentTotal, point);
        }
        if (isWithinDateInterval(date.substring(0, 10), compareDateRange)) {
          metrics.addToMetricPoint(compareTotal, point);
        }
      }
    }
    return { currentTotal, compareTotal };
  }, [
    chartableData.series,
    dateRange,
    compareDateRange,
    timeGranularity,
    user.timezone,
  ]);

  const customizableMetricsArray = useMemo(
    // Remove metrics that are explicitly hidden and forecasted metrics
    () => {
      const filteredMetrics = metricsArray.filter(
        (metric) =>
          !metric.hideFromChartDisplay &&
          !metric.name.toLocaleLowerCase().includes("forecast"),
      );

      return filteredMetrics.map(({ name, ...rest }) => ({
        ...rest,
        title: name,
        name: normalizeMetricKey(name),
      }));
    },
    [metricsArray],
  );

  const filteredMetricsArray = useMemo(
    // Remove metrics that aren't in the customization modal.
    () => {
      let graphMetricsToFilterOn = customizableMetricsArray.map(
        (metric) => metric.name,
      );

      if (graphMetricBoxes.length === 0) {
        setGraphMetricBoxes(graphMetricsToFilterOn);
      } else {
        graphMetricsToFilterOn = graphMetricBoxes;
      }

      const metricsSet = new Set(graphMetricsToFilterOn);
      // Filters out metrics that doesn't exist on cash or accrual model

      const filteredMetrics = customizableMetricsArray.filter(({ name }) => {
        return metricsSet.has(name);
      });

      return filteredMetrics.sort(
        (a, b) =>
          graphMetricBoxes.indexOf(a.name) - graphMetricBoxes.indexOf(b.name),
      );
    },
    [customizableMetricsArray, graphMetricBoxes, setGraphMetricBoxes],
  );

  const legendMetrics: LegendMetric[] = useMemo(
    (): LegendMetric[] =>
      filteredMetricsArray.map(
        ({
          changeFractionAreaTextForTabDisplay,
          name,
          title,
          isPositiveChangeGood,
          formatter,
          format,
          calculate,
          descriptionHTML,
          hideComparisonFromTabDisplay = false,
        }): LegendMetric => ({
          hideComparisonFromTabDisplay,
          name,
          title,
          isPositiveChangeGood,
          format,
          formatter: (v: number | null | undefined) =>
            formatter(v, user.currencies.currentCurrency, {
              precision: "approximate",
            }),
          currentValue: calculate(baseMetricLegends.currentTotal),
          compareValue: calculate(baseMetricLegends.compareTotal),
          description: descriptionHTML,
          changeFractionAreaText: changeFractionAreaTextForTabDisplay,
        }),
      ),
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [filteredMetricsArray, baseMetricLegends],
  );

  const legendMetricsSeries = useMemo(() => {
    const leafRows: ChartableResponseRowWithChildren[] = leaves(chartableData);
    const rv: TimeSeriesChartProps[] = [];

    for (const metric of filteredMetricsArray) {
      const format = metric.format;
      const yLabel = metric.name;
      const xAxisFormat = "date";
      const serieses: TimeSeries[] = [];
      for (const leafRow of leafRows) {
        const overallName =
          selectedMetricIndices.length > 1 ? metric.name : "All";

        const name =
          leafRow.description.length === 0
            ? overallName
            : leafRow.description
                .map((v) => v.value || "(not set)")
                .join(" / ");

        let dates: string[] = [];
        if (timeGranularity !== "hourly") {
          dates = Array.from(iterateOverInterval(dateRange));
        } else if (timeGranularity === "hourly") {
          dates = Array.from(
            iterateHourlyOverIntervalWithAdditionalAccuracy(
              dateRange,
              user.timezone,
            ),
          );
        }

        let value = 0;
        const points: TimeSeriesPoint[] = dates.map((date) => {
          const metricPoint = leafRow.series[date];
          const calc = metricPoint ? metric.calculate(metricPoint) : 0.0;
          if (metric.displayCumulativeForChart) {
            value += calc;
          } else {
            value = calc;
          }
          return { date, value };
        });

        const chartSeries: TimeSeries = { name, points };

        // Compare value
        if (metric.calculateCompare) {
          let compareValue = 0;
          chartSeries.comparePoints = dates.map((date) => {
            const metricPoint = leafRow.series[date];
            const compareValue_ = metricPoint
              ? metric.calculateCompare?.(metricPoint) ?? 0.0
              : 0.0;
            if (metric.displayCumulativeForChart) {
              compareValue += compareValue_;
            } else {
              compareValue = compareValue_;
            }
            return { date, value: compareValue };
          });
        }

        // Band values
        if (metric.calculateBand) {
          chartSeries.bandPoints = [[], []];
          let lo = 0;
          let hi = 0;
          for (const date of dates) {
            if (!isWithinDateInterval(date, dateRange)) {
              continue;
            }
            const metricPoint = leafRow.series[date];
            const [lo_, hi_] = metricPoint
              ? metric.calculateBand(metricPoint)
              : [0, 0];
            if (metric.displayCumulativeForChart) {
              lo += lo_;
              hi += hi_;
            } else {
              lo = lo_;
              hi = hi_;
            }
            chartSeries.bandPoints[0].push({
              date,
              value: isNaN(lo) ? "Infinity" : lo,
            });
            chartSeries.bandPoints[1].push({
              date,
              value: isNaN(hi) ? "Infinity" : hi,
            });
          }
        }

        serieses.push(chartSeries);
      }
      const chartProps: TimeSeriesChartProps = {
        format,
        yLabel,
        xAxisFormat,
        serieses,
        noLegend: true,
      };
      if (metric.chartCompareSuffix) {
        chartProps.compareSuffix = metric.chartCompareSuffix;
      }
      if (metric.chartBandSuffix) {
        chartProps.bandSuffix = metric.chartBandSuffix;
      }
      rv.push(chartProps);
    }

    return rv;
  }, [
    chartableData,
    filteredMetricsArray,
    selectedMetricIndices.length,
    timeGranularity,
    dateRange,
    user.timezone,
  ]);

  const selectMetric = (index: number) => {
    const metric = legendMetrics[index];
    logEvent("Selects Top Level Metric", { Metric: metric.name });

    if (selectedMetricIndices.includes(index)) {
      setSelectedMetricIndices(
        selectedMetricIndices.filter((i) => i !== index),
      );
    } else {
      if (selectedMetricIndices.length <= 1) {
        setSelectedMetricIndices([...selectedMetricIndices, index]);
      } else {
        setSelectedMetricIndices([selectedMetricIndices[1], index]);
      }
    }
  };

  const selectedChartPropsArray = useMemo(() => {
    if (
      selectedMetricIndices.every((index) => index < legendMetricsSeries.length)
    ) {
      return selectedMetricIndices.map(
        (selectedIndex) => legendMetricsSeries[selectedIndex],
      );
    } else {
      setSelectedMetricIndices([0]);
      return [legendMetricsSeries[0]];
    }
  }, [selectedMetricIndices, legendMetricsSeries]);

  const selectedTableForExport: Table = useMemo(() => {
    const dimensionColumns: ColumnDef[] = [
      {
        key: "category",
        name: "Category",
      },
      {
        key: "date",
        name: "Date",
      },
    ];

    const factColumns: ColumnDef[] = legendMetricsSeries.map(({ yLabel }) => ({
      key: yLabel,
      name: yLabel,
    }));

    const keyToMetrics: Record<string, Record<string, any>> = {};
    for (const { serieses, yLabel: metric } of legendMetricsSeries) {
      for (const series of serieses) {
        const category = series.name;
        for (const { date, value } of series.points) {
          const key = jsonHash({ category, date });
          if (!keyToMetrics[key]) {
            keyToMetrics[key] = {};
          }
          keyToMetrics[key][metric] = value;
        }
      }
    }

    const values = chain(keyToMetrics)
      .toPairs()
      .map(([key, ms]) => ({ ...ms, ...JSON.parse(key) }))
      .sortBy(["date", "category"])
      .value();
    const rows: TableRow[] = values.map((vs) => ({
      link: {},
      rowMetadata: {},
      export: vs,
      display: vs,
      changeFractions: {},
      cellHoverHTML: {},
    }));

    return {
      dimensionColumns,
      factColumns,
      rows,
      title: "",
    };
  }, [legendMetricsSeries]);

  return (
    <div className="bg-white rounded-md border px-4">
      <ChartLegend
        legendMetrics={
          currentSalesViewEdit
            ? legendMetrics
            : // default loading metrics view
              [DefaultLegendMetric(user.currencies.currentCurrency)]
        }
        selectMetric={selectMetric}
        selectedMetricIndices={selectedMetricIndices}
        customizationModal={
          <CustomizeChartMetricsModal
            graphMetricBoxes={graphMetricBoxes}
            setGraphMetricBoxes={setGraphMetricBoxes}
            customizableMetricsArray={customizableMetricsArray}
            setSelectedMetricIndices={setSelectedMetricIndices}
          />
        }
      />
      <div data-test="sales-charts">
        {selectedChartPropsArray.map((selectedChartProps) => (
          <div className="py-4" key={selectedChartProps.yLabel}>
            <div className="flex items-center justify-content-between mb-4">
              <h4>{selectedChartProps.yLabel}</h4>
              <ExportToCsv
                reportType="Sales Timeseries"
                fileName="north-beam-sales-report-ts.csv"
                data={selectedTableForExport}
              />
            </div>
            <TimeSeriesChart
              {...selectedChartProps}
              plugins={[annotationPlugin]}
              additionalDatasets={[annotationDataset]}
              height={50}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
